import { ButtonDesigns, ButtonTypes } from '@paywithextend/components'
import queryString from 'query-string'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import { LayoutShellButton } from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ReceiptToolsLayoutShellButton from 'src/components/ReceiptToolsDropdown/ReceiptToolsLayoutShellButton'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import ExpensesAppRoute from 'src/navigation/appRoutes/expenses.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  expenseSettingsUrl,
  expensesExpenseReportsUrl,
  expensesOutOfPocketReportsUrl,
  expensesTransactionsUrl,
} from 'src/navigation/urls'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsDownloadExpenseReportsPaidPopupOpen,
} from 'src/redux/actions/app.actions'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectExpensesAllReportsMatch,
  selectExpensesExpenseReportsMatch,
  selectExpensesNeedsApprovalMatch,
  selectExpensesTransactionsMatch,
  selectExpensesYourReportsMatch,
  selectReimbursementsRouteMatch,
  selectSubmitExpensesRouteMatch,
} from 'src/redux/selectors/router.selectors'

const Expenses: React.FC = () => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const dispatch = useBackgroundDispatch()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const expensesExpenseReportsMatch = useSelector(
    selectExpensesExpenseReportsMatch,
  )
  const expensesTransactionsMatch = useSelector(selectExpensesTransactionsMatch)
  const expensesNeedsApprovalMatch = useSelector(
    selectExpensesNeedsApprovalMatch,
  )
  const expensesAllExpenseReportsMatch = useSelector(
    selectExpensesAllReportsMatch,
  )
  const expensesYourExpenseReportsMatch = useSelector(
    selectExpensesYourReportsMatch,
  )
  const submitExpensesMatch = useSelector(selectSubmitExpensesRouteMatch)
  const reimbursementsMatch = useSelector(selectReimbursementsRouteMatch)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const user = useCurrentUser()
  const headerText =
    expensesTransactionsMatch || expensesNeedsApprovalMatch
      ? 'Card Transactions'
      : expensesExpenseReportsMatch
        ? 'Statements'
        : submitExpensesMatch
          ? 'Submit Expenses'
          : reimbursementsMatch
            ? 'Reimbursements'
            : 'Expenses'

  // const subtitleText =
  //   expensesTransactionsMatch
  //     ? 'View all transactions across your organization for transparency and better spend visibility'
  //     : undefined

  const expensesExpenseReportsMatchButtons =
    user?.hasOrgAdminPermissions && user?.organizationId
      ? [
          {
            design: ButtonDesigns.designCtaInverse,
            type: ButtonTypes.button,
            text: translate('Expense Settings'),
            linkProps: {
              to: expenseSettingsUrl(user.organizationId),
              ariaLabel: 'Expense Settings',
            },
          },
        ]
      : []

  const submitExpensesMatchButtons = [
    {
      design: ButtonDesigns.designCta,
      type: ButtonTypes.button,
      text: translate('New Report'),
      onClick: (): void => {
        dispatch(createSetIsCreateExpenseReportPopupOpen(true))
        analytics?.track('create OOPE flyout - click-in')
      },
    },
  ]

  const handleDownloadReportsOnReimbursementPage = (): void => {
    const queryParams = new URLSearchParams(window.location.search)

    // Get filters Params here
    const filtersBody = {
      search: queryParams.get('search') || undefined,
      sinceTimestamp: queryParams.get('sinceTimestamp') || undefined,
      untilTimestamp: queryParams.get('untilTimestamp') || undefined,
      statuses:
        queryParams.getAll('statuses').length > 0
          ? queryParams.getAll('statuses')
          : undefined,
      userIds:
        queryParams.getAll('userIds').length > 0
          ? queryParams.getAll('userIds')
          : undefined,
    }
    dispatch(createSetIsDownloadExpenseReportsPaidPopupOpen(true, filtersBody))
  }

  const reimbursementsButtons = [
    {
      design: ButtonDesigns.designCtaInverse,
      type: ButtonTypes.button,
      text: translate('Download Reports'),
      onClick: handleDownloadReportsOnReimbursementPage,
    },
  ]

  let buttons: LayoutShellButton[] = []
  if (expensesExpenseReportsMatch) {
    buttons = expensesExpenseReportsMatchButtons
  } else if (submitExpensesMatch) {
    buttons = submitExpensesMatchButtons
  } else if (reimbursementsMatch) {
    buttons = reimbursementsButtons
  } else {
    buttons = []
  }

  if (
    (expensesTransactionsMatch || expensesNeedsApprovalMatch) &&
    receiptManagementEnabled &&
    organization !== undefined
  ) {
    buttons = [
      ...buttons,
      <ReceiptToolsLayoutShellButton
        key={'receipt-tools-layout-button'}
        organization={organization}
        hasReceiptRulePermissions={!!user?.hasOrgBookkeeperPermissions}
      />,
    ]
  }

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
    ])
  }, [dispatchStatistics])

  return expensesAllExpenseReportsMatch !== null ||
    expensesYourExpenseReportsMatch !== null ? (
    <Rdrct
      to={{
        pathname: expensesOutOfPocketReportsUrl(),
        state: {},
        search: queryString.stringify({
          ...queryString.parse(calculatedBackgroundLocation.search),
          type: expensesAllExpenseReportsMatch !== null ? 'all' : 'yours',
        }),
        hash: '',
      }}
    />
  ) : (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={[]}
      content={
        <Switch location={calculatedBackgroundLocation}>
          {ExpensesAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...ExpensesAppRoute.routeProps} exact>
            <Rdrct
              to={
                user?.hasOrgBookkeeperPermissions
                  ? expensesExpenseReportsUrl()
                  : expensesTransactionsUrl()
              }
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={translate(headerText)}
      // subtitle={(subtitleText && translate(subtitleText)) || undefined}
      /* @ts-ignore next-line */
      buttons={buttons}
    />
  )
}

export default Expenses
